import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ResizablePanels from "@/components/ui/ResizablePanels";

import SecuritySettings from "./components/SecuritySettings/index";
import EmojiCustomizer from "./components/EmojiCustomizer";
import CharacterMap from "./components/CharacterMap";
import KeyboardAssignment from "./components/KeyboardAssignment";
import CharacterPreviewPanel from "./components/RightPanels/CharacterPreviewPanel";
import EmojiSelectionPanel from "./components/RightPanels/EmojiSelectionPanel";
import MappingReferencePanel from "./components/RightPanels/MappingReferencePanel";
import CipherSummaryPanel from "./components/RightPanels/CipherSummaryPanel";
import CompleteCipherPanel from "./components/RightPanels/CompleteCipherPanel";

import { apiClient } from "@/lib/apiClient";
import { getErrorMessage } from "@/lib/api";
import type { CipherMapping } from "@/lib/types/cipher";

type SecurityLevel = "basic" | "protected" | "verified";
type CoreSecurityLevel = "basic" | "standard" | "advanced";
type Step = "character_map" | "emoji_map" | "assignment" | "security" | "review";

interface SelectedEmoji {
  char: string;
  code: string;
}

interface CipherMapState {
  characters: {
    set: string[];
    count: number;
    selection: {
      language: string;
      additionalSets: string[];
    };
  };
  emojis: {
    set: SelectedEmoji[];
  };
  mapping: CipherMapping;
}

interface CipherCreatorProps {
  initialStep?: Step;
}

const SECURITY_TO_CORE: Record<SecurityLevel, CoreSecurityLevel> = {
  basic: "basic",
  protected: "standard",
  verified: "advanced",
};

export default function CipherCreator({
  initialStep = "character_map",
}: CipherCreatorProps) {
  const [searchParams] = useSearchParams();
  const cipherCode = searchParams.get("cipherCode");
  const isEditing = !!cipherCode;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState<Step>(
    isEditing ? "review" : initialStep,
  );
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>("basic");
  const [passcode, setPasscode] = useState("");
  const [userSelectedCipherCode, setUserSelectedCipherCode] =
    useState<string>("");
  const [cipherMap, setCipherMap] = useState<CipherMapState>({
    characters: {
      set: [],
      count: 0,
      selection: { language: "english", additionalSets: [] },
    },
    emojis: { set: [] },
    mapping: { forward: {}, reverse: {} },
  });
  const [cipherName, setCipherName] = useState("");
  const [error, setError] = useState("");
  const [isAssignmentValid, setIsAssignmentValid] = useState(false);

  // Hydrate when editing an existing cipher. The new API only allows
  // metadata updates (name, security level) — the mapping is immutable
  // post-creation, so the wizard becomes a read-only review for the mapping
  // and an editor for the metadata.
  useQuery({
    queryKey: ["cipher", cipherCode],
    queryFn: async () => {
      if (!cipherCode) return null;
      const cipher = await apiClient.getCipher(cipherCode);
      setCipherName(cipher.name);
      const reverseSecurity: Record<CoreSecurityLevel, SecurityLevel> = {
        basic: "basic",
        standard: "protected",
        advanced: "verified",
      };
      setSecurityLevel(reverseSecurity[cipher.security_level]);
      if ("mapping" in cipher && cipher.mapping) {
        setCipherMap((prev) => ({
          ...prev,
          mapping: cipher.mapping as CipherMapping,
        }));
      }
      return cipher;
    },
    enabled: !!cipherCode,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const code = userSelectedCipherCode || undefined;
      return apiClient.createCipher({
        name: cipherName,
        mapping: {
          forward: cipherMap.mapping.forward,
          reverse: cipherMap.mapping.reverse,
          version: "1.0.0",
          ...(code ? { cipher_code: code } : {}),
        },
        security_level: SECURITY_TO_CORE[securityLevel],
        passcode:
          securityLevel === "protected" && passcode ? passcode : undefined,
        status: "active",
      });
    },
    onSuccess: () => {
      toast.success("Cipher created successfully!");
      queryClient.invalidateQueries({ queryKey: ["ciphers"] });
      navigate("/ciphers", { state: { activeTab: "library" } });
    },
    onError: (err: unknown) => {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!cipherCode) throw new Error("No cipher code");
      return apiClient.updateCipher(cipherCode, {
        name: cipherName,
        security_level: SECURITY_TO_CORE[securityLevel],
      });
    },
    onSuccess: () => {
      toast.success("Cipher updated!");
      queryClient.invalidateQueries({ queryKey: ["ciphers"] });
      queryClient.invalidateQueries({ queryKey: ["cipher", cipherCode] });
      navigate("/ciphers", { state: { activeTab: "library" } });
    },
    onError: (err: unknown) => {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    },
  });

  const validateStep = (step: Step): boolean => {
    setError("");
    switch (step) {
      case "character_map":
        if (!cipherName.trim()) {
          setError("Please provide a name for your cipher");
          return false;
        }
        if (cipherMap.characters.set.length === 0) {
          setError("Please select at least one character set");
          return false;
        }
        return true;
      case "assignment":
        if (!isAssignmentValid) {
          setError(
            "Please ensure all characters are properly mapped to emojis",
          );
          return false;
        }
        return true;
      case "security":
        if (securityLevel === "protected" && passcode.trim().length !== 4) {
          setError(
            "Please provide a 4-digit passcode for protected security level",
          );
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSecurityChange = (level: SecurityLevel, code?: string) => {
    setSecurityLevel(level);
    setPasscode(level === "protected" ? code || "" : "");
  };

  const handleNext = () => {
    let nextStep: Step;
    switch (currentStep) {
      case "character_map":
        nextStep = "emoji_map";
        break;
      case "emoji_map":
        nextStep = "assignment";
        break;
      case "assignment":
        nextStep = "security";
        break;
      case "security":
        nextStep = "review";
        break;
      default:
        return;
    }
    if (!validateStep(currentStep)) return;
    setCurrentStep(nextStep);
  };

  const handleBack = () => {
    let prevStep: Step;
    switch (currentStep) {
      case "emoji_map":
        prevStep = "character_map";
        break;
      case "assignment":
        prevStep = "emoji_map";
        break;
      case "security":
        prevStep = "assignment";
        break;
      case "review":
        prevStep = isEditing ? "assignment" : "security";
        break;
      default:
        return;
    }
    setCurrentStep(prevStep);
  };

  const handleSave = () => {
    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const handleSaveCustomEmojis = (emojis: SelectedEmoji[]) => {
    setCipherMap({ ...cipherMap, emojis: { set: emojis } });
    setTimeout(() => handleNext(), 0);
  };

  const handleCharacterMapChange = (
    characters: string[],
    language: string,
    additionalSets: string[],
  ) => {
    setCipherMap({
      ...cipherMap,
      characters: {
        set: characters,
        count: characters.length,
        selection: { language, additionalSets },
      },
    });
  };

  const handleMappingChange = (mapping: CipherMapState["mapping"]) =>
    setCipherMap((prev) => ({ ...prev, mapping }));

  const handleEditSection = (section: Step) => {
    if (isEditing && section === "security") return;
    setCurrentStep(section);
  };

  const handleRemoveEmoji = (index: number) => {
    setCipherMap((prev) => ({
      ...prev,
      emojis: {
        ...prev.emojis,
        set: prev.emojis.set.filter((_, i) => i !== index),
      },
    }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const renderContent = () => {
    switch (currentStep) {
      case "character_map":
        return (
          <CharacterMap
            onCharacterSetChange={handleCharacterMapChange}
            onNameChange={setCipherName}
            onNext={handleNext}
            initialLanguage={cipherMap.characters.selection.language}
            initialSets={cipherMap.characters.selection.additionalSets}
            initialName={cipherName}
          />
        );
      case "emoji_map":
        return (
          <EmojiCustomizer
            onSave={handleSaveCustomEmojis}
            onCancel={() => setCurrentStep("character_map")}
            onSelectionChange={(emojis) =>
              setCipherMap((prev) => ({
                ...prev,
                emojis: { ...prev.emojis, set: emojis },
              }))
            }
            initialEmojis={cipherMap.emojis.set}
            initialName="Emoji Map"
            parentName="Emoji Map"
            requiredCount={cipherMap.characters.set.length}
          />
        );
      case "assignment":
        return (
          <KeyboardAssignment
            characters={cipherMap.characters.set}
            emojis={cipherMap.emojis.set}
            onMappingChange={handleMappingChange}
            onValidationChange={setIsAssignmentValid}
          />
        );
      case "security":
        if (isEditing) {
          setCurrentStep("review");
          return null;
        }
        return (
          <SecuritySettings
            defaultLevel={securityLevel}
            defaultPasscode={passcode}
            onSecurityChange={handleSecurityChange}
          />
        );
      case "review":
        return (
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold">Review Your Cipher</h3>
              <p className="text-xs text-muted-foreground">
                — Confirm before deploying
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-sm truncate">{cipherName}</span>
              <span className="text-gray-300">|</span>
              <span className="capitalize text-gray-600">{securityLevel}</span>
              {securityLevel === "protected" && passcode && (
                <span className="text-green-600">✓</span>
              )}
              <div className="ml-auto flex items-center gap-1">
                {!isEditing && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSection("character_map")}
                      className="h-5 text-[10px] px-1.5 gap-0.5"
                    >
                      <Settings className="h-2.5 w-2.5" /> Chars
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSection("emoji_map")}
                      className="h-5 text-[10px] px-1.5 gap-0.5"
                    >
                      <Settings className="h-2.5 w-2.5" /> Emojis
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSection("security")}
                      className="h-5 text-[10px] px-1.5 gap-0.5"
                    >
                      <Settings className="h-2.5 w-2.5" /> Security
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-semibold text-gray-500">
                  Assignment Preview
                </h3>
                {!isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditSection("assignment")}
                    className="h-5 text-[10px] px-1.5 gap-0.5"
                  >
                    <Settings className="h-2.5 w-2.5" /> Edit
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-6 gap-0.5 bg-gray-50 p-1.5 rounded-lg max-h-[calc(100vh-500px)] overflow-y-auto">
                {cipherMap.characters.set.map((char) => (
                  <div
                    key={char}
                    className="flex flex-col items-center justify-center"
                  >
                    <span className="font-medium text-[10px] text-gray-500">
                      {char === " " ? "␣" : char}
                    </span>
                    <span className="text-sm">
                      {cipherMap.mapping.forward[char]?.targetEmoji || "❓"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderRightPanel = () => {
    switch (currentStep) {
      case "character_map":
        return (
          <CharacterPreviewPanel
            characters={cipherMap.characters.set}
            language={cipherMap.characters.selection.language}
            additionalSets={cipherMap.characters.selection.additionalSets}
          />
        );
      case "emoji_map":
        return (
          <EmojiSelectionPanel
            selectedEmojis={cipherMap.emojis.set}
            requiredCount={cipherMap.characters.set.length}
            onRemove={handleRemoveEmoji}
          />
        );
      case "assignment":
        return (
          <MappingReferencePanel
            characters={cipherMap.characters.set}
            mapping={cipherMap.mapping}
          />
        );
      case "security":
        return (
          <CipherSummaryPanel
            cipherName={cipherName}
            securityLevel={securityLevel}
            hasPasscode={passcode.trim().length === 4}
          />
        );
      case "review":
        return (
          <CompleteCipherPanel
            cipherName={cipherName}
            cipherCode={userSelectedCipherCode || cipherCode || undefined}
            onCipherCodeChange={setUserSelectedCipherCode}
          />
        );
      default:
        return null;
    }
  };

  const getRightPanelTitle = () => {
    switch (currentStep) {
      case "character_map":
        return "Character Preview";
      case "emoji_map":
        return "Selected Emojis";
      case "assignment":
        return "Mapping Reference";
      case "security":
        return "Cipher Summary";
      case "review":
        return "Complete Cipher";
      default:
        return "Preview";
    }
  };

  const renderActionBar = () => {
    const showBack = currentStep !== "character_map";
    const isLastStep = currentStep === "review";
    return (
      <div className="flex items-center justify-between gap-4">
        {showBack ? (
          <Button
            variant="outline"
            size="lg"
            onClick={handleBack}
            className="flex-1"
          >
            ← Back
          </Button>
        ) : (
          <div />
        )}
        {isLastStep ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-1">
                  <Button
                    size="lg"
                    onClick={handleSave}
                    className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold"
                    disabled={isSaving || !cipherName.trim()}
                  >
                    {isSaving
                      ? "Deploying..."
                      : isEditing
                        ? "Update Cipher"
                        : "Deploy Cipher ✨"}
                  </Button>
                </span>
              </TooltipTrigger>
              {!cipherName.trim() && (
                <TooltipContent>
                  <p>Please provide a name for your cipher</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            size="lg"
            onClick={handleNext}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
          >
            Next:{" "}
            {currentStep === "character_map"
              ? "Emojis"
              : currentStep === "emoji_map"
                ? "Assignment"
                : currentStep === "assignment"
                  ? "Security"
                  : "Review"}{" "}
            →
          </Button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Create Your Cipher
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Follow these steps to create your custom emoji cipher
        </p>
      </div>

      <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 rounded-full mb-6" />

      <div className="flex items-center w-full mb-6">
        {(
          [
            { key: "character_map" as Step, label: "Characters" },
            { key: "emoji_map" as Step, label: "Emojis" },
            { key: "assignment" as Step, label: "Assignment" },
            { key: "security" as Step, label: "Security" },
            { key: "review" as Step, label: "Review" },
          ]
        ).map((step, index, arr) => {
          const steps: Step[] = [
            "character_map",
            "emoji_map",
            "assignment",
            "security",
            "review",
          ];
          const currentIndex = steps.indexOf(currentStep);
          const stepIndex = steps.indexOf(step.key);
          const isActive = step.key === currentStep;
          const isCompleted = stepIndex < currentIndex;

          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-purple-600 text-white"
                      : isCompleted
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  {isCompleted ? "✓" : index + 1}
                </div>
                <span
                  className={`text-xs mt-1 ${
                    isActive
                      ? "font-semibold text-purple-600"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < arr.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-1 ${
                    stepIndex < currentIndex
                      ? "bg-green-500"
                      : "bg-gray-200 dark:bg-gray-700"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="h-[clamp(400px,calc(100vh-320px),900px)]">
        <ResizablePanels
          leftPanel={renderContent()}
          rightPanel={renderRightPanel()}
          leftTitle="Configuration"
          rightTitle={getRightPanelTitle()}
          leftActionBar={renderActionBar()}
        />
      </div>

      <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-pink-500 to-purple-600 rounded-full mt-6" />
    </div>
  );
}
